import { loadBaseCSS } from '../shared/button-utils.js';
import {
  escapeHtml,
  getContentRendererStyles,
  getViewportBreakpoint,
  isBlockVisible,
  mountContentBlock,
  normalizeContentDocument,
  resolveRegistry,
  sanitizeHtml,
  sortBlocksForMode
} from '../content-blocks/qds-content-blocks.js';
import type { QdsContentDocument } from '../../types/content.js';
import type { QdsBlockRegistry, QdsRenderContext } from '../../types/blocks.js';

loadBaseCSS();

/**
 * @element qds-content-renderer
 *
 * Renders a content document for runtime or preview display, mounting each visible block into a responsive page-flow or dashboard-grid layout and adapting to the host width via a ResizeObserver.
 *
 * @attr {string} mode - Rendering mode: `runtime` (default) or `preview`
 *
 * @property {QdsContentDocument | null} document - The content document to render
 * @property {QdsBlockRegistry | null} registry - The block registry used to resolve block definitions
 * @property {QdsRenderContext | undefined} context - Host-supplied render context (registry, capabilities)
 * @property {'runtime' | 'preview'} mode - The rendering mode
 */
export class QdsContentRenderer extends HTMLElement {
  private _document: QdsContentDocument | null = null;
  private _registry: QdsBlockRegistry | null = null;
  private _context?: QdsRenderContext;
  private _mode: 'runtime' | 'preview' = 'runtime';
  private _resizeObserver?: ResizeObserver;
  private _hostWidth = 1440;

  static get observedAttributes(): string[] {
    return ['mode'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected) {
      return;
    }

    this._observeSize();
    this.render();
  }

  disconnectedCallback(): void {
    this._resizeObserver?.disconnect();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'mode') {
      this._mode = newValue === 'preview' ? 'preview' : 'runtime';
      this.render();
    }
  }

  get document(): QdsContentDocument | null {
    return this._document;
  }

  set document(value: QdsContentDocument | null) {
    this._document = value;
    this.render();
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this.render();
  }

  get context(): QdsRenderContext | undefined {
    return this._context;
  }

  set context(value: QdsRenderContext | undefined) {
    this._context = value;
    this.render();
  }

  get mode(): 'runtime' | 'preview' {
    return this._mode;
  }

  set mode(value: 'runtime' | 'preview') {
    this._mode = value === 'preview' ? 'preview' : 'runtime';
    this.setAttribute('mode', this._mode);
  }

  private _observeSize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this._resizeObserver?.disconnect();
    this._resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      this._hostWidth = Math.max(320, Math.round(entry.contentRect.width));
      this.render();
    });
    this._resizeObserver.observe(this);
  }

  private _renderEmptyState(message: string): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        ${getContentRendererStyles()}

        .renderer-empty {
          padding: 32px 24px;
          border: 1px dashed var(--theme-border-color, #e0e0e0);
          border-radius: 12px;
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
          text-align: center;
        }
      </style>
      <div class="renderer-empty">${message}</div>
    `;
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    if (!this._document) {
      this._renderEmptyState('No content document has been provided.');
      return;
    }

    const registry = resolveRegistry(this._registry || this._context?.blockRegistry || null);
    const documentModel = normalizeContentDocument(this._document, registry);
    const breakpoint = getViewportBreakpoint(this._hostWidth || this.getBoundingClientRect().width || window.innerWidth);
    const visibleBlocks = sortBlocksForMode(documentModel.blocks, documentModel.mode)
      .filter((block) => isBlockVisible(block, breakpoint));
    const hasBodyHtml = Boolean(documentModel.bodyHtml);
    const hasVisibleBlocks = visibleBlocks.length > 0;

    if (!hasBodyHtml && !hasVisibleBlocks) {
      this._renderEmptyState('This document does not contain any visible content yet.');
      return;
    }

    const dashboardColumns = documentModel.layout?.columns || 12;
    const dashboardGap = documentModel.layout?.gap || '16px';
    const dashboardRowHeight = `${documentModel.layout?.rowHeight || 128}px`;

    this.shadowRoot.innerHTML = `
      <style>
        ${getContentRendererStyles()}

        .renderer {
          display: grid;
          gap: 24px;
          width: 100%;
          max-width: ${documentModel.layout?.maxWidth || 'min(1400px, 100%)'};
          margin: 0 auto;
        }

        .renderer__header {
          display: grid;
          gap: 12px;
        }

        .renderer__title {
          margin: 0;
          color: var(--theme-text-primary, #333333);
          font-size: clamp(1.65rem, 2vw + 1rem, 2.6rem);
          line-height: 1.1;
        }

        .renderer__summary {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: clamp(1rem, 0.4vw + 0.95rem, 1.15rem);
          max-width: 78ch;
        }

        .renderer__body {
          padding: 20px 24px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 12px;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
        }

        .renderer__body > :first-child {
          margin-top: 0;
        }

        .renderer__body > :last-child {
          margin-bottom: 0;
        }

        .renderer__flow {
          display: grid;
          gap: 20px;
        }

        .renderer__block {
          display: block;
        }

        .renderer__dashboard {
          display: grid;
          gap: ${dashboardGap};
          grid-template-columns: repeat(${dashboardColumns}, minmax(0, 1fr));
          grid-auto-rows: ${dashboardRowHeight};
          align-items: start;
        }

        .renderer__dashboard-item {
          min-width: 0;
        }

        @media (max-width: 1200px) {
          .renderer__dashboard {
            grid-template-columns: repeat(6, minmax(0, 1fr));
          }
        }

        @media (max-width: 720px) {
          .renderer {
            gap: 20px;
          }

          .renderer__body {
            padding: 16px;
          }

          .renderer__dashboard {
            grid-template-columns: repeat(1, minmax(0, 1fr));
            grid-auto-rows: auto;
          }

          .renderer__dashboard-item {
            grid-column: auto !important;
            grid-row: auto !important;
          }
        }
      </style>
      <section class="renderer" data-mode="${documentModel.mode}" data-renderer-mode="${this._mode}">
        ${(documentModel.title || documentModel.summary) ? `
          <header class="renderer__header">
            ${documentModel.title ? `<h1 class="renderer__title">${escapeHtml(documentModel.title)}</h1>` : ''}
            ${documentModel.summary ? `<p class="renderer__summary">${escapeHtml(documentModel.summary)}</p>` : ''}
          </header>
        ` : ''}
        ${documentModel.bodyHtml ? `<div class="renderer__body" id="renderer-body">${sanitizeHtml(documentModel.bodyHtml)}</div>` : ''}
        ${documentModel.mode === 'dashboard'
          ? `<div class="renderer__dashboard">
              ${visibleBlocks.map((block) => `
                <div
                  class="renderer__dashboard-item"
                  data-block-id="${block.id}"
                  style="grid-column: ${Math.max(1, (block.layout?.x || 0) + 1)} / span ${Math.max(1, block.layout?.w || 1)}; grid-row: ${Math.max(1, (block.layout?.y || 0) + 1)} / span ${Math.max(1, block.layout?.h || 1)};"
                ></div>
              `).join('')}
            </div>`
          : `<div class="renderer__flow">
              ${visibleBlocks.map((block) => `<article class="renderer__block" data-block-id="${block.id}"></article>`).join('')}
            </div>`}
      </section>
    `;

    visibleBlocks.forEach((block) => {
      const mountPoint = this.shadowRoot?.querySelector(`[data-block-id="${block.id}"]`) as HTMLElement | null;
      if (!mountPoint) {
        return;
      }

      mountContentBlock(mountPoint, {
        block,
        contentDocument: documentModel,
        registry,
        context: this._context || { blockRegistry: registry },
        renderMode: this._mode,
        breakpoint,
        readonly: true
      });
    });
  }
}

if (!customElements.get('qds-content-renderer')) {
  customElements.define('qds-content-renderer', QdsContentRenderer);
}

export default QdsContentRenderer;
